import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Pencil, Trash2, Upload, Download, Search } from 'lucide-react';
import { PositionMappingService, type PositionMapping, type CreatePositionMappingData } from '@/services/PositionMappingService';
import { CsvImportDialog } from '@/components/admin/CsvImportDialog';
import { MappingTester } from '@/components/admin/MappingTester';
import { SampleDataImporter } from '@/components/admin/SampleDataImporter';

export default function AdminPositionMappings() {
  const navigate = useNavigate();
  const [mappings, setMappings] = useState<PositionMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCsvImportDialog, setShowCsvImportDialog] = useState(false);
  const [editingMapping, setEditingMapping] = useState<PositionMapping | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    symbol: '',
    tv_to_ctrader_ratio: '',
    contract_size: '',
    base_unit: '',
    category: 'forex' as const,
    is_active: true
  });

  useEffect(() => {
    loadMappings();
  }, []);

  const loadMappings = async () => {
    try {
      setLoading(true);
      const data = await PositionMappingService.getAllMappings();
      setMappings(data);
    } catch (error) {
      toast.error('Failed to load position mappings');
      console.error('Error loading mappings:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      symbol: '',
      tv_to_ctrader_ratio: '',
      contract_size: '',
      base_unit: '',
      category: 'forex',
      is_active: true
    });
    setEditingMapping(null);
  };

  const handleAddMapping = async () => {
    try {
      const data: CreatePositionMappingData = {
        symbol: formData.symbol.toUpperCase(),
        tv_to_ctrader_ratio: parseFloat(formData.tv_to_ctrader_ratio),
        contract_size: parseFloat(formData.contract_size) || 1,
        base_unit: formData.base_unit,
        category: formData.category,
        is_active: formData.is_active
      };

      await PositionMappingService.createMapping(data);
      toast.success('Position mapping created successfully');
      setShowAddDialog(false);
      resetForm();
      loadMappings();
    } catch (error) {
      toast.error('Failed to create position mapping');
      console.error('Error creating mapping:', error);
    }
  };

  const handleUpdateMapping = async () => {
    if (!editingMapping) return;

    try {
      const data = {
        id: editingMapping.id,
        symbol: formData.symbol.toUpperCase(),
        tv_to_ctrader_ratio: parseFloat(formData.tv_to_ctrader_ratio),
        contract_size: parseFloat(formData.contract_size) || 1,
        base_unit: formData.base_unit,
        category: formData.category as any,
        is_active: formData.is_active
      };

      await PositionMappingService.updateMapping(data);
      toast.success('Position mapping updated successfully');
      setEditingMapping(null);
      resetForm();
      loadMappings();
    } catch (error) {
      toast.error('Failed to update position mapping');
      console.error('Error updating mapping:', error);
    }
  };

  const handleDeleteMapping = async (id: string, symbol: string) => {
    if (!confirm(`Are you sure you want to delete the mapping for ${symbol}?`)) return;

    try {
      await PositionMappingService.deleteMapping(id);
      toast.success('Position mapping deleted successfully');
      loadMappings();
    } catch (error) {
      toast.error('Failed to delete position mapping');
      console.error('Error deleting mapping:', error);
    }
  };

  const handleEditMapping = (mapping: PositionMapping) => {
    setEditingMapping(mapping);
    setFormData({
      symbol: mapping.symbol,
      tv_to_ctrader_ratio: mapping.tv_to_ctrader_ratio.toString(),
      contract_size: mapping.contract_size.toString(),
      base_unit: mapping.base_unit,
      category: mapping.category as any,
      is_active: mapping.is_active
    });
    setShowAddDialog(true);
  };

  const exportMappings = () => {
    const dataStr = JSON.stringify(mappings, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `position_mappings_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredMappings = mappings.filter(mapping => {
    const matchesSearch = mapping.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         mapping.base_unit.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || mapping.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const categories = [...new Set(mappings.map(m => m.category))];

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Admin
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Position Mappings Management</h1>
            <p className="text-muted-foreground">Manage TradingView to cTrader position mappings</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowCsvImportDialog(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Import CSV
          </Button>
          <Button variant="outline" onClick={exportMappings}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="h-4 w-4 mr-2" />
                Add Mapping
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {editingMapping ? 'Edit Position Mapping' : 'Add New Position Mapping'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="symbol">Symbol</Label>
                  <Input
                    id="symbol"
                    value={formData.symbol}
                    onChange={(e) => setFormData(prev => ({ ...prev, symbol: e.target.value }))}
                    placeholder="e.g., EURUSD, BTCUSDT"
                    className="uppercase"
                  />
                </div>
                <div>
                  <Label htmlFor="ratio">TV to cTrader Ratio</Label>
                  <Input
                    id="ratio"
                    type="number"
                    step="0.000001"
                    value={formData.tv_to_ctrader_ratio}
                    onChange={(e) => setFormData(prev => ({ ...prev, tv_to_ctrader_ratio: e.target.value }))}
                    placeholder="e.g., 0.01, 1"
                  />
                </div>
                <div>
                  <Label htmlFor="contractSize">Contract Size</Label>
                  <Input
                    id="contractSize"
                    type="number"
                    value={formData.contract_size}
                    onChange={(e) => setFormData(prev => ({ ...prev, contract_size: e.target.value }))}
                    placeholder="e.g., 100000, 1"
                  />
                </div>
                <div>
                  <Label htmlFor="baseUnit">Base Unit</Label>
                  <Input
                    id="baseUnit"
                    value={formData.base_unit}
                    onChange={(e) => setFormData(prev => ({ ...prev, base_unit: e.target.value }))}
                    placeholder="e.g., EUR, BTC"
                  />
                </div>
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, category: value as any }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="forex">Forex</SelectItem>
                      <SelectItem value="crypto">Crypto</SelectItem>
                      <SelectItem value="commodity">Commodity</SelectItem>
                      <SelectItem value="stock">Stock</SelectItem>
                      <SelectItem value="index">Index</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="rounded"
                  />
                  <Label htmlFor="isActive">Active</Label>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={editingMapping ? handleUpdateMapping : handleAddMapping}>
                    {editingMapping ? 'Update' : 'Create'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search symbols..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(category => (
                  <SelectItem key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Sample Data Importer */}
      <SampleDataImporter onImportComplete={loadMappings} />

      {/* Mapping Tester */}
      <MappingTester />

      {/* Mappings Table */}
      <Card>
        <CardHeader>
          <CardTitle>Position Mappings ({filteredMappings.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading mappings...</div>
          ) : filteredMappings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No mappings found matching your filters
            </div>
          ) : (
            <div className="space-y-4">
              {filteredMappings.map((mapping) => (
                <div key={mapping.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-lg">{mapping.symbol}</span>
                          <Badge variant={mapping.is_active ? 'default' : 'secondary'}>
                            {mapping.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          <Badge variant="outline">{mapping.category}</Badge>
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          Base Unit: {mapping.base_unit}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-sm text-muted-foreground">TV → cTrader Ratio</div>
                        <div className="font-medium">{mapping.tv_to_ctrader_ratio}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-muted-foreground">Contract Size</div>
                        <div className="font-medium">{mapping.contract_size.toLocaleString()}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditMapping(mapping)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteMapping(mapping.id, mapping.symbol)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* CSV Import Dialog */}
      <CsvImportDialog
        open={showCsvImportDialog}
        onOpenChange={setShowCsvImportDialog}
        onImportComplete={loadMappings}
      />
    </div>
  );
}